import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {UpdateArchivableParams} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {assertAndContinue} from 'Asserts';
import {ExecutionPlan} from 'studies/progress/Types';
import {ExperimentMethodPlanREST} from 'studies/typed/process-development/Types';
import {patchJSON, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export type SourcePMEIIdsByTemplatePMDVIdREST = Record<string, Array<string>>;
export interface ExperimentConditionREST {
  replicateInfos: Array<{
    replicateNumber: number;
    // Used to add experiment method instance associations for the new condition
    sourcePMEIIdsByTemplatePMDVId?: SourcePMEIIdsByTemplatePMDVIdREST;
  }>;
  methodPlans: Array<ExperimentMethodPlanREST>;
  name: string;
  description?: string;
}

// Keep in sync with benchling/procedures/network/rest/procedure_run_rest_specs.py
export interface CreateProcedureRunParams {
  description?: string; // Todo BNCH-128851: Remove optional once frontend is created
  folderId: string;
  templateProcedureFlowchartVersionId: string;
  organizationId: string;
  experimentConditions: Array<ExperimentConditionREST>;
  studyId?: string;
}

export interface CreateProcedureRunResponse {
  createdId: string;
}

export async function createProcedureRun(
  procedureRun: CreateProcedureRunParams
): Promise<CreateProcedureRunResponse> {
  const creates = [procedureRun];
  const {createdIds} = await postJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.PROCEDURE_RUN),
    data: {creates},
  });
  assertAndContinue(createdIds.length === 1, 'Expected exactly one procedure run created');
  return {createdId: createdIds[0]};
}

export interface UpdateProcedureRunParams extends UpdateArchivableParams {
  id: string;
  description?: string;
  newConditions?: Array<ExperimentConditionREST>;
}

export interface TaskResponse extends TaskRunner.OngoingTaskResponse {
  taskId: string;
  timingMeta: ExecutionPlan;
}

export interface UpdateProcedureRunResponse {
  updatedIds: Array<string>;
}

export async function updateProcedureRun(
  procedureRun: UpdateProcedureRunParams,
  {supportsAsync}: {supportsAsync: boolean}
): Promise<
  | {async: false; data: UpdateProcedureRunResponse}
  | {async: true; data: TaskResponse; taskMeta: TaskRunner.TaskMeta}
> {
  const updates = [procedureRun];
  const url = `${getApiBaseForObjectType(DomainGraphType.PROCEDURE_RUN)}?supportsAsync=${
    supportsAsync ? 1 : 0
  }`;
  // We use legacy patchJSON because net.patchJSONPromise / TaskRunner.runTask are too limited: we need to
  // inspect the response status code / headers since our response is dynamic and may either be a
  // synchronous web request or an asynchronous celery task, where our UX will reflect the asynchrony.
  const xhr: JQueryXHR = patchJSON({
    url,
    data: {updates},
  }) as JQueryXHR;
  const response = await Promise.resolve(xhr);
  if (xhr.status === 202) {
    return {
      async: true,
      data: response as TaskResponse,
      taskMeta: TaskRunner.getTaskMeta(xhr, url),
    };
  } else {
    return {
      async: false,
      data: response as UpdateProcedureRunResponse,
    };
  }
}
